/**
 * Presentational price display for listing cards and detail pages.
 * Shows strikethrough original price + current price + "Price Drop" badge when
 * previousPrice > price; otherwise a single current price (gold).
 */

const GOLD = '#C89F3C';
const MUTED = '#9ca3af';
const BADGE_BG = 'rgba(229, 115, 115, 0.14)';
const BADGE_COLOR = '#c0392b';

const isFinitePrice = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
};

const formatPrice = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n % 1 === 0 ? String(n) : n.toFixed(2);
};

// Three distinct, softened colours (Jason, 2026-10-20): none of them the
// gold price, the green match banner, the blue actions, or red.
const MODE_PILL = {
  open: { bg: '#4F9D97', color: 'white' },   // muted teal
  trade: { bg: '#6F7FB8', color: 'white' },  // muted indigo, well clear of the rose
  sell: { bg: '#B3728C', color: 'white' },   // muted rose
};

/** The offer-mode pill on its own: `large` for the listing page. */
export const OfferModePill = ({ mode, large = false, style = {} }) => {
  const key = mode === 'trade' || mode === 'sell' ? mode : 'open';
  return (
    <span
      className="pedal-offer-mode-pill"
      title={key === 'trade' ? 'Trade offers only' : key === 'sell' ? 'Offers to buy only' : 'Trade offers and offers to buy'}
      style={{
        display: 'inline-flex', alignItems: 'center', flexShrink: 0,
        padding: large ? '6px 14px' : '4px 10px',
        borderRadius: '9999px', backgroundColor: MODE_PILL[key].bg, color: MODE_PILL[key].color,
        fontWeight: 700, fontSize: large ? '0.95rem' : '0.78rem', fontFamily: 'Nunito, sans-serif',
        letterSpacing: '0.01em', whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {key === 'trade' ? 'Trade only' : key === 'sell' ? 'Sale only' : 'Any offer'}
    </span>
  );
};

const PedalPriceDisplay = ({
  price,
  previousPrice = null,
  layout = 'standalone',
  className = '',
  style = {},
  showDropBadge = true,
  dashboardTile = false,
  // Offer mode (plan §5, Jason 2026-10-20): a coloured pill to the right of
  // the price ("Any offer" grey, "Trade only" gold, "Sale only" blue). Lives in
  // the card's text section because the thumbnail already carries badges.
  offerMode = null,
  // Desktop tile grids (Trade Opportunities / Saved Pedals pages, 2026-10-31):
  // adds a class that, at 769px and up only (index.css), reserves two lines
  // for the price + pill block so side-by-side cards share a height whether
  // or not the pill had to wrap under a price-drop cluster. Markup, flow and
  // wording are unchanged at every width; nothing changes on mobile.
  desktopReserveRows = false,
}) => {
  const current = Number(price);
  if (!isFinitePrice(current)) return null;

  const modeEl = offerMode ? <OfferModePill mode={offerMode} /> : null;
  const withMode = (node) => (modeEl
    ? <span className={desktopReserveRows ? 'pedal-price-with-mode pedal-price-with-mode--desktop-reserve' : 'pedal-price-with-mode'} style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px', minWidth: 0, maxWidth: '100%' }}>{node}{modeEl}</span>
    : node);

  const previous = previousPrice != null ? Number(previousPrice) : null;
  const showDrop =
    isFinitePrice(current) &&
    isFinitePrice(previous) &&
    previous > current;

  const currentPriceEl = (
    <span
      className={className}
      style={{
        fontWeight: 700,
        color: GOLD,
        whiteSpace: 'nowrap',
        ...(showDrop && layout === 'row' ? { flexShrink: 0 } : {}),
        ...(showDrop ? {} : style),
      }}
    >
      ${formatPrice(current)}
    </span>
  );

  if (!showDrop) {
    if (layout === 'detail') {
      return (
        <span style={{ lineHeight: '1.2', ...style }}>
          <strong>Price:</strong> {withMode(currentPriceEl)}
        </span>
      );
    }
    return withMode(currentPriceEl);
  }

  const isRow = layout === 'row';

  const dropCluster = (
    <span
      className="pedal-price-cluster"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: isRow ? 'nowrap' : 'wrap',
        gap: isRow ? '4px' : '6px',
        minWidth: 0,
        maxWidth: isRow ? '100%' : undefined,
        overflow: isRow ? 'hidden' : undefined,
        ...style,
      }}
    >
      <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
        Price dropped from ${formatPrice(previous)} to ${formatPrice(current)}
      </span>
      <span
        className={dashboardTile && isRow ? 'pedal-price-previous dashboard-pedal-card-price-previous' : 'pedal-price-previous'}
        style={{
          fontWeight: 600,
          color: MUTED,
          textDecoration: 'line-through',
          whiteSpace: 'nowrap',
          ...(!(dashboardTile && isRow)
            ? { fontSize: isRow ? '0.8em' : '0.9em' }
            : {}),
          ...(isRow
            ? { flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }
            : {}),
        }}
        aria-hidden="true"
      >
        ${formatPrice(previous)}
      </span>
      {currentPriceEl}
      {showDropBadge && (
        <span
          className={dashboardTile && isRow ? 'dashboard-pedal-card-price-drop-badge' : undefined}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: dashboardTile && isRow ? '2px 8px' : isRow ? '1px 5px' : '2px 8px',
            borderRadius: '9999px',
            backgroundColor: BADGE_BG,
            color: BADGE_COLOR,
            fontWeight: 700,
            fontSize: dashboardTile && isRow ? '0.65rem' : isRow ? '0.55rem' : '0.65rem',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
          }}
        >
          {dashboardTile || !isRow ? 'Price Drop' : 'Drop'}
        </span>
      )}
    </span>
  );

  if (layout === 'detail') {
    return (
      <span style={{ lineHeight: '1.2' }}>
        <strong>Price:</strong> {withMode(dropCluster)}
      </span>
    );
  }

  if (layout === 'row') {
    // The price cluster stays on one line; the pill may wrap under it (never
    // clipped by the cluster's overflow: hidden).
    return withMode(
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          flex: '1 1 auto',
        }}
      >
        {dropCluster}
      </span>
    );
  }

  // standalone — left column, no sibling pill in this row
  return withMode(dropCluster);
};

export default PedalPriceDisplay;
